import { Link } from 'react-router-dom';
import { Wrench, CheckCircle, Phone, ArrowRight, Cog, Droplets, Gauge } from 'lucide-react';
import SEO from '../components/SEO';

const Reparation = () => {
  const equipmentTypes = [
    'Pelles hydrauliques',
    'Mini-pelles',
    'Chargeuses sur pneus',
    'Chargeuses sur chenilles',
    'Bulldozers',
    'Compacteurs',
    'Nacelles élévatrices',
    'Chariots élévateurs'
  ];

  const brands = ['LiuGong', 'Komatsu', 'Caterpillar', 'Kubota', 'Hitachi', 'Volvo', 'JCB', 'Bobcat'];

  const processSteps = [
    {
      number: '01',
      title: 'Diagnostic',
      description: 'Analyse complète de votre engin pour identifier les problèmes mécaniques et hydrauliques.'
    },
    {
      number: '02',
      title: 'Devis Détaillé',
      description: 'Établissement d\'un devis précis avec le détail des pièces et de la main d\'œuvre.'
    },
    {
      number: '03',
      title: 'Réparation',
      description: 'Intervention par nos techniciens qualifiés avec des pièces de qualité.'
    },
    {
      number: '04',
      title: 'Contrôle',
      description: 'Tests et vérifications pour garantir le bon fonctionnement de votre engin.'
    }
  ];

  const services = [
    {
      icon: <Cog size={28} />,
      title: 'Mécanique Générale',
      items: ['Moteur diesel', 'Transmission', 'Embrayage', 'Direction']
    },
    {
      icon: <Droplets size={28} />,
      title: 'Hydraulique',
      items: ['Pompes hydrauliques', 'Vérins', 'Distributeurs', 'Flexibles']
    },
    {
      icon: <Gauge size={28} />,
      title: 'Électrique',
      items: ['Démarreur', 'Alternateur', 'Tableau de bord', 'Capteurs']
    }
  ];

  return (
    <div data-testid="reparation-page">
      <SEO
        title="Réparation Engins de Chantier La Réunion - Mécanique & Hydraulique"
        description="Réparation mécanique et hydraulique d'engins de chantier à La Réunion. Pelles hydrauliques, mini-pelles, chargeuses, bulldozers — toutes marques : LiuGong, Komatsu, Caterpillar, Kubota, Hitachi, Volvo. Diagnostic gratuit. Saint-Leu, intervention toute l'île."
        canonical="/reparation"
        image="https://images.unsplash.com/photo-1664312616511-81fe2e745cb3?auto=format&fit=crop&w=1200&q=80"
      />
      {/* Hero Section */}
      <section className="hero-section hero-reparation min-h-[60vh]">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl animate-slide-up">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Notre Service
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Réparation Mécanique d'Engins
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Nos techniciens qualifiés interviennent sur tous types d'engins de chantier. 
              Diagnostic précis et réparations durables.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="reparation-cta">
              Demander un Devis
            </Link>
          </div>
        </div>
      </section>

      {/* Equipment Types Section */}
      <section className="section-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span 
                className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Engins Pris en Charge
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-8"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Tous Types d'Engins de Chantier
              </h2>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {equipmentTypes.map((type, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-[#F5C500] flex-shrink-0" />
                    <span className="text-gray-700">{type}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#F4F4F5] p-8">
              <h3 
                className="text-2xl font-bold text-[#1a1a1a] mb-6"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Marques Prises en Charge
              </h3>
              <div className="flex flex-wrap gap-3">
                {brands.map((brand, index) => (
                  <span 
                    key={index} 
                    className="bg-white px-4 py-2 text-[#1a1a1a] font-semibold border-l-4 border-[#F5C500]"
                  >
                    {brand}
                  </span>
                ))}
              </div>
              <p className="text-gray-600 mt-6">
                Et bien d'autres marques... Contactez-nous pour vérifier la compatibilité avec votre engin.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="section-gray py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Domaines d'Intervention
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a]"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Nos Compétences Techniques
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white p-8 border-l-4 border-[#F5C500]"
                data-testid={`service-detail-${index}`}
              >
                <div className="icon-container mb-6">
                  {service.icon}
                </div>
                <h3 
                  className="text-xl font-bold text-[#1a1a1a] mb-4"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {service.title}
                </h3>
                <ul className="space-y-2">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-600">
                      <ArrowRight size={16} className="text-[#F5C500]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Notre Méthode
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a]"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Processus d'Intervention
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative" data-testid={`process-step-${index}`}>
                <div className="step-number mb-4">{step.number}</div>
                <h3 
                  className="text-xl font-bold text-[#1a1a1a] mb-3"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {step.title}
                </h3>
                <p className="text-gray-600">{step.description}</p>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-6 left-[60px] w-[calc(100%-60px)] h-0.5 bg-[#F5C500]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1a1a1a] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Votre Engin a Besoin d'une Réparation ?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un diagnostic gratuit. Nos techniciens sont à votre disposition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Demander un Devis
            </Link>
            <a href="tel:0693526449" className="btn-secondary flex items-center justify-center gap-2">
              <Phone size={20} />
              06 93 52 64 49
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reparation;
